import React, { useState, useMemo, useRef, useEffect } from "react";
import styled from "styled-components";
import {
  ElementContainer,
  FlexibleContainer,
  RowContainer,
} from "../../../components/containers/styledComponents";
import { greyText, lightGreyOutline } from "../../../styles";
import {
  Icon,
  IconContainer,
} from "../../../components/visuals/styledComponents";
import { ToggleAccordionBlueText } from "../../../components/buttons/ToggleAccordionBlueText";
import { CompassParagraph } from "../../../components/common/styledComponents";
import {
  NA100_VALUE_MAP,
  NA100_VALUE_MAP_TOP_LEVEL,
  NA100_metrics,
  Na100IndicatorValue,
  NA100TreeNode,
} from "./NA100Constants";
import {
  Na100Package,
  PublicMetric,
} from "../../../models/CompanyprofileModels";
import { getNA100Metric } from "./util";

const StyledRowContainer = styled(RowContainer)`
  width: 90%;
  padding-bottom: 10px;
  border-bottom: 1px solid ${lightGreyOutline};
`;

const getIconFromValue = (value: string, isChild = false): string => {
  const indicatorValue = value as Na100IndicatorValue;
  const map = isChild ? NA100_VALUE_MAP : NA100_VALUE_MAP_TOP_LEVEL;
  return map[indicatorValue] || map[Na100IndicatorValue.NotAvailable];
};

export const NA100Accordion = ({
  metricsData,
  node,
  dataPackage,
}: {
  metricsData: PublicMetric[];
  node: NA100TreeNode;
  dataPackage: Na100Package;
}): JSX.Element => {
  const legendRef = useRef<HTMLDivElement | null>(null);
  const [isActive, setIsActive] = useState(false);

  const { metricName, value } = useMemo(() => {
    return getNA100Metric<NA100_metrics>(
      node.id as NA100_metrics,
      dataPackage,
      metricsData
    );
  }, [node.id, metricsData]);

  const scrollToLegend = (): void => {
    legendRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "nearest",
    });
  };

  useEffect(() => {
    if (isActive) {
      scrollToLegend();
    }
  }, [isActive]);

  const hasChildren = node.children && node.children.length > 0;

  return (
    <StyledRowContainer ref={legendRef} jc="flex-start" ai="flex-start">
      <FlexibleContainer fd="column" ai="flex-start">
        <ElementContainer ai="center" jc="flex-start">
          <IconContainer size={35}>
            <Icon src={getIconFromValue(value)} />
          </IconContainer>

          {hasChildren ? (
            <ToggleAccordionBlueText
              textActive={metricName}
              textInactive={metricName}
              handleChange={(): void => setIsActive(!isActive)}
              color={greyText}
              fontSize={14}
              bold={false}
            />
          ) : (
            <CompassParagraph color={greyText} fontSize={14} ml={6}>
              {metricName}
            </CompassParagraph>
          )}
        </ElementContainer>

        {/* Accordion content */}
        {isActive &&
          hasChildren &&
          node.children?.map((childNode) => (
            <ChildLevelAccordionItem
              key={childNode.id}
              node={childNode}
              metricsData={metricsData}
              dataPackage={dataPackage}
            />
          ))}
      </FlexibleContainer>
    </StyledRowContainer>
  );
};

const ChildLevelAccordionItem = ({
  node,
  metricsData,
  dataPackage,
}: {
  node: NA100TreeNode;
  metricsData: PublicMetric[];
  dataPackage: Na100Package;
}): JSX.Element => {
  const [isActive, setIsActive] = useState(false);

  const { metricName, value } = useMemo(() => {
    return getNA100Metric<NA100_metrics>(
      node.id as NA100_metrics,
      dataPackage,
      metricsData
    );
  }, [node.id, metricsData]);

  const hasChildren = node.children && node.children.length > 0;

  return (
    <FlexibleContainer fd="column" ai="flex-start" ml="24px" mt="8px" w="100%">
      <ElementContainer ai="center" jc="flex-start">
        <IconContainer size={30}>
          <Icon src={getIconFromValue(value, true)} />
        </IconContainer>

        {hasChildren ? (
          <ToggleAccordionBlueText
            textActive={metricName}
            textInactive={metricName}
            handleChange={(): void => setIsActive(!isActive)}
            color={greyText}
            fontSize={13}
            bold={false}
          />
        ) : (
          <CompassParagraph color={greyText} fontSize={13} ml={6}>
            {metricName}
          </CompassParagraph>
        )}
      </ElementContainer>

      {/* Child accordion content */}
      {isActive &&
        hasChildren &&
        node.children?.map((grandchildNode) => (
          <GrandchildLevelAccordionItem
            key={grandchildNode.id}
            node={grandchildNode}
            metricsData={metricsData}
            dataPackage={dataPackage}
          />
        ))}
    </FlexibleContainer>
  );
};

const GrandchildLevelAccordionItem = ({
  node,
  metricsData,
  dataPackage,
}: {
  node: NA100TreeNode;
  metricsData: PublicMetric[];
  dataPackage: Na100Package;
}): JSX.Element => {
  const { metricName, value } = useMemo(() => {
    return getNA100Metric<NA100_metrics>(
      node.id as NA100_metrics,
      dataPackage,
      metricsData
    );
  }, [node.id, metricsData]);

  return (
    <FlexibleContainer fd="column" ai="flex-start" ml="40px" mt="6px" w="100%">
      <ElementContainer ai="center" jc="flex-start">
        <IconContainer size={28}>
          <Icon src={getIconFromValue(value, true)} />
        </IconContainer>

        <CompassParagraph color={greyText} fontSize={12} ml={6}>
          {metricName}
        </CompassParagraph>
      </ElementContainer>
    </FlexibleContainer>
  );
};
